// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
// SPDX-License-Identifier: AGPL-3.0-or-later

import './styles.css';
import { initTooltip, hideTooltip } from './tooltip';
import { initGlossary } from './glossary';
import { openAbout } from './components/about';
import { closeOverlay } from './components/drawer';
import { renderCycle } from './views/cycle';
import { renderToday } from './views/today';
import { renderMap, destroyMap } from './views/map';
import { renderHistory } from './views/history';
import { renderSuburbs } from './views/suburbs';
import { renderBrands } from './views/brands';
import { renderRebrands } from './views/rebrands';
import { renderSpread } from './views/spread';
import { esc } from './format';

interface View { id: string; label: string; render: (root: HTMLElement) => Promise<void> }

// Tab labels are words only — never counts. Counts belong inside the views.
const VIEWS: View[] = [
  { id: 'cycle', label: 'Cycle', render: renderCycle },
  { id: 'today', label: 'Today', render: renderToday },
  { id: 'map', label: 'Map', render: renderMap },
  { id: 'history', label: 'History', render: renderHistory },
  { id: 'suburbs', label: 'Suburbs', render: renderSuburbs },
  { id: 'brands', label: 'Brands', render: renderBrands },
  { id: 'rebrands', label: 'Rebrands', render: renderRebrands },
  { id: 'spread', label: 'Spread', render: renderSpread },
];

const LOGO = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
  <path d="M3 21V5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v16"/><path d="M2 21h13"/>
  <path d="M13 9h3a2 2 0 0 1 2 2v6a1.5 1.5 0 0 0 3 0V9l-2.5-3"/><path d="M6 8h4"/>
</svg>`;

function shell(): void {
  const app = document.getElementById('app')!;
  app.innerHTML = `
    <header class="site-header">
      <div class="header-inner">
        <a class="brand" href="#cycle" aria-label="Fuel Prices — home">
          ${LOGO}<span>Fuel Prices</span><span class="brand-sub">WA &amp; QLD</span>
        </a>
        <nav class="nav-tabs" id="nav" aria-label="Views">
          ${VIEWS.map((v) => `<button type="button" class="nav-tab" data-view="${v.id}">${esc(v.label)}</button>`).join('')}
        </nav>
        <span class="header-spacer"></span>
        <button type="button" class="icon-btn" id="about-btn" aria-label="About this site and its data">?</button>
      </div>
    </header>
    <main class="main-content" id="main" tabindex="-1"></main>
    <footer class="site-footer">
      <div class="footer-inner">
        <p>
          Prices from <a href="https://catalogue.data.wa.gov.au/dataset/fuelwatch-historic-fuel-prices" target="_blank" rel="noopener">FuelWatch</a>
          (Department of Energy, Mines, Industry Regulation and Safety, Western Australia) and
          <a href="https://www.data.qld.gov.au/dataset/fuel-price-reporting-2026" target="_blank" rel="noopener">Fuel Price Reporting</a>
          (Queensland Treasury), both CC BY 4.0. Boundaries from the
          <a href="https://www.abs.gov.au/statistics/standards/australian-statistical-geography-standard-asgs-edition-3" target="_blank" rel="noopener">ABS ASGS 2021</a>, CC BY 4.0.
          Not affiliated with any of them.
        </p>
        <p>Built by <a href="https://benrichardson.dev/">benrichardson.dev</a> · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a></p>
      </div>
    </footer>
  `;

  document.getElementById('nav')!.addEventListener('click', (e) => {
    const btn = (e.target as Element).closest('.nav-tab') as HTMLElement | null;
    if (!btn) return;
    window.location.hash = btn.dataset.view ?? 'cycle';
  });
  document.getElementById('about-btn')!.addEventListener('click', () => { void openAbout(); });
}

let current = '';

async function route(): Promise<void> {
  const id = (window.location.hash.replace(/^#/, '').split('?')[0]) || 'cycle';
  const view = VIEWS.find((v) => v.id === id) ?? VIEWS[0];
  if (view.id === current) return;
  current = view.id;

  closeOverlay();
  hideTooltip();
  if (view.id !== 'map') destroyMap();

  document.querySelectorAll('.nav-tab').forEach((b) => {
    const active = (b as HTMLElement).dataset.view === view.id;
    if (active) b.setAttribute('aria-current', 'page');
    else b.removeAttribute('aria-current');
  });
  document.title = view.id === 'cycle' ? 'Fuel Prices' : `${view.label} · Fuel Prices`;

  const main = document.getElementById('main')!;
  main.innerHTML = '<div class="skeleton"></div>';
  try {
    await view.render(main);
  } catch (err) {
    main.innerHTML = `
      <div class="error-box">
        <p>Could not load this view.</p>
        <p class="brand-sub">${esc(err instanceof Error ? err.message : String(err))}</p>
        <button type="button" id="retry">Try again</button>
      </div>`;
    document.getElementById('retry')?.addEventListener('click', () => { current = ''; void route(); });
  }
}

shell();
initTooltip();
initGlossary();
window.addEventListener('hashchange', () => { void route(); });
void route();
